import React, { useState } from 'react';
import './PrivateHospitalCost.scss';

/**
 * 「私家醫院醫療費用」, an in-house replacement for the Piktochart of the same name, in the look of
 * ai.portfoplus.com: the Piktochart's three bars (a stent, late-stage bowel cancer, targeted drugs)
 * and its two figures (cost growth, public waiting time). The figures are the Piktochart's own,
 * kept as they are. The targeted-drug bar is a slider over months of treatment, since the drugs are
 * taken for a long time; at one month it is the Piktochart's bar.
 */

type Item = 'stent' | 'bowel' | 'drug';

export const ITEMS: Item[] = ['stent', 'bowel', 'drug'];
/** Cost in HK$ ten-thousands (萬); the Piktochart's figures. A range is [low, high]. */
export const COST: Record<Item, [number, number]> = { stent: [20, 20], bowel: [129.3, 129.3], drug: [1.5, 17] };
/** The Piktochart's axis runs to 1,200k, i.e. 120萬. */
const AXIS_MAX = 135;
const TICKS = [0, 20, 40, 60, 80, 100, 120];
const MAX_MONTHS = 6;

interface Intl {
  locale: string;
  formatMessage: (descriptor: { id: string }, values?: Record<string, string | number>) => string;
}

/** The app's `zh` is Simplified Chinese; `lang` picks the CJK font in the stylesheet. */
const HTML_LANG: Record<string, string> = { zh: 'zh-CN', 'zh-Hant-HK': 'zh-HK' };

const PrivateHospitalCost = ({ intl }: { intl: Intl }) => {
  const t = (id: string, values?: Record<string, string | number>) => intl.formatMessage({ id }, values);
  const [months, setMonths] = useState<number>(1);

  /** 萬 in Chinese, k in English. */
  const money = (wan: number) =>
    intl.locale === 'en'
      ? `$${(Math.round(wan * 100) / 10).toLocaleString('en')}k`
      : `$${Math.round(wan * 10) / 10}${t('phc-wan')}`;

  const range = (item: Item) => {
    const [low, high] = COST[item];
    const k = item === 'drug' ? months : 1;
    return low === high && item !== 'drug' ? money(high) : `${money(low * k)}-${money(high * k)}`;
  };
  const width = (item: Item) =>
    `${Math.min(100, ((COST[item][1] * (item === 'drug' ? months : 1)) / AXIS_MAX) * 100)}%`;

  return (
    <div className="private-hospital-cost" lang={HTML_LANG[intl.locale] || intl.locale}>
      <h2 className="phc-title">
        <span>{t('phc-title')}</span>
      </h2>

      <section className="phc-chart">
        <ul className="phc-bars">
          {ITEMS.map(item => (
            <li key={item} className={`phc-row is-${item}`}>
              <span className="phc-name">
                {t(`phc-${item}-1`)}
                <b>{t(`phc-${item}-2`)}</b>
              </span>
              <span className="phc-track">
                <span className="phc-bar" style={{ width: width(item) }} />
              </span>
              <span className="phc-value">
                <strong>
                  {item === 'drug' && months === 1 ? `${range(item)}${t('phc-per-month-suffix')}` : range(item)}
                </strong>
                <small>
                  {item === 'drug'
                    ? months === 1
                      ? t('phc-drug-note')
                      : t('phc-drug-total', { n: months })
                    : item === 'bowel'
                    ? t('phc-bowel-note')
                    : ''}
                </small>
              </span>
            </li>
          ))}
        </ul>
        <div className="phc-axis" aria-hidden="true">
          {TICKS.map(tick => (
            <i key={tick} style={{ left: `${(tick / AXIS_MAX) * 100}%` }}>
              {tick === 0 ? 0 : money(tick).replace('$', '')}
            </i>
          ))}
        </div>
      </section>

      <label className="phc-slider">
        <span aria-live="polite">
          {t('phc-months', { n: months, low: money(COST.drug[0] * months), high: money(COST.drug[1] * months) })}
        </span>
        <input
          type="range"
          min={1}
          max={MAX_MONTHS}
          value={months}
          onChange={e => setMonths(Number(e.target.value))}
          aria-label={t('phc-months-label')}
        />
        <small>{t('phc-months-note')}</small>
      </label>

      <div className="phc-stats">
        <div>
          <h3>{t('phc-cost-head')}</h3>
          <strong className="is-teal">{t('phc-cost-value')}</strong>
        </div>
        <div>
          <h3>{t('phc-wait-head')}</h3>
          <strong className="is-navy">{t('phc-wait-value')}</strong>
        </div>
      </div>

      <p className="phc-source">
        {t('phc-source')}
        <span>{t('phc-source-1')}</span>
        <span>{t('phc-source-2')}</span>
        <span>{t('phc-source-3')}</span>
      </p>
    </div>
  );
};

export default PrivateHospitalCost;
